// https://auth0.com/docs/quickstart/spa/react/01-login

import React from 'react';
import Immutable from 'immutable';

const ACTIONS = {
  REFRESH_STARTED: 'refresh_started',
  REFRESH_SUCCESS: 'refresh_success',
  REFRESH_ERROR: 'refresh_error',
  CREATE_SUCCESS: 'create_success',
  CREATE_STARTED: 'create_started',
  CREATE_ERROR: 'create_error',
  PUT_SUCCESS: 'put_success',
  PUT_STARTED: 'put_started',
  PUT_ERROR: 'put_error',
};

const INITIAL_STATE = Immutable.fromJS({
  positionsByProfessionalLeagueId: [],
  loading: true,
  updating: false,
  creating: false,
  error: false,
});

const DataContext = React.createContext(null);
const DispatchContext = React.createContext(null);

const positionsReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.REFRESH_STARTED: {
      return state.set('loading', true);
    }
    case ACTIONS.REFRESH_SUCCESS: {
      const byId = {};
      action.positions.forEach(s => byId[s.id] = s);
      return state
        .setIn(['positionsByProfessionalLeagueId', action.professionalLeagueId], byId)
        .set('error', false)
        .set('loading', false);
    }
    case ACTIONS.REFRESH_ERROR: {
      return state
        .set('loading', false)
        .set('error', action.error);
    }
    case ACTIONS.CREATE_STARTED: {
      return state.set('creating', true);
    }
    case ACTIONS.CREATE_SUCCESS: {
      return state
        .set('creating', false)
        .set('error', false)
        .setIn(['positionsByProfessionalLeagueId', action.professionalLeagueId, action.position.id], action.position);
    }
    case ACTIONS.CREATE_ERROR: {
      return state
        .set('creating', false)
        .set('error', action.error);
    }
    case ACTIONS.PUT_STARTED: {
      return state.set('updating', true);
    }
    case ACTIONS.PUT_SUCCESS: {
      return state
        .set('updating', false)
        .set('error', false)
        .setIn(['positionsByProfessionalLeagueId', action.professionalLeagueId, action.position.id], action.position);
    }
    case ACTIONS.PUT_ERROR: {
      return state
        .set('updating', false)
        .set('error', action.error);
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
};

export const refreshPositions = async (dispatch, fetchAuth, professionalLeagueId) => {
  dispatch({ type: ACTIONS.REFRESH_STARTED, professionalLeagueId });
  const positions = await fetchAuth(`/api/v1/professional_leagues/${professionalLeagueId}/positions`);
  dispatch({
    type: ACTIONS.REFRESH_SUCCESS,
    positions,
    professionalLeagueId,
  });
}

export const createPosition = async (dispatch, fetchAuth, professionalLeagueId, attrs) => {
  dispatch({ type: ACTIONS.CREATE_STARTED, professionalLeagueId });
  if (!Array.isArray(attrs.positions)) attrs.positions = [];
  try {
    const position = await fetchAuth(`/api/v1/professional_leagues/${professionalLeagueId}/positions`, {
      method: 'POST',
      body: attrs,
    });
    dispatch({
      type: ACTIONS.CREATE_SUCCESS,
      position,
      professionalLeagueId,
    });
  } catch (e) {
    dispatch({
      type: ACTIONS.CREATE_ERROR,
      error: e,
      professionalLeagueId,
    });
    throw e;
  }
}

export const putPosition = async (dispatch, fetchAuth, professionalLeagueId, body) => {
  dispatch({ type: ACTIONS.PUT_STARTED, professionalLeagueId });
  try {
    const position = await fetchAuth(`/api/v1/professional_leagues/${professionalLeagueId}/positions/${body.id}`, {
      method: 'PUT',
      body,
    });
    dispatch({
      type: ACTIONS.PUT_SUCCESS,
      position,
      professionalLeagueId,
    });
  } catch (e) {
    dispatch({
      type: ACTIONS.PUT_ERROR,
      error: e,
      professionalLeagueId,
    });
    throw e;
  }
}

export const PositionsProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(positionsReducer, INITIAL_STATE);
  return (
    <DataContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </DataContext.Provider>
  )
}

export const usePositions = () => {
  const dataC = React.useContext(DataContext);
  const dispC = React.useContext(DispatchContext);
  if (!dataC || !dispC) throw new Error('usePositions must be used within a PositionsProvider');

  return [dataC.toJS(), dispC];
}
